import React, { useState, useRef, useEffect, lazy, Suspense } from "react";
import "./App.css";
import { useForm } from "react-hook-form";
import { Amplify } from 'aws-amplify';
import amplifyconfig from './amplifyconfiguration.json';
import { submitRegistration } from './api';
import LogoOnly from "./LogoOnly";
import CookieConsent from "./components/CookieConsent";
import OrnateBackground from "./components/OrnateBackground";
import Footer from "./components/Footer";
import LoadingSpinner from "./components/LoadingSpinner";
import { SessionsProvider } from "./context/SessionsContext";
import { COUNTRIES } from "./data/countries";
import { getReferralSource } from "./utils/referralSource";
import { buildShopUrl } from "./utils/buildShopUrl";
import { logger } from "./utils/logger";
import { EVENT_INFO, PRIVACY_URLS, DISCLAIMERS, EMAIL_PROVIDERS, RESTRICTED_EMAIL_DOMAINS } from "./constants/appConfig";
import { useSkin } from "./skins/useSkin";

// Lazy load route components for code splitting
const Dashboard = lazy(() => import("./Dashboard"));
const CheckIn = lazy(() => import("./CheckIn"));
const AllExperiences = lazy(() => import("./components/experiences/AllExperiences"));
const SingleExperience = lazy(() => import("./components/experiences/SingleExperience"));
const BookingForm = lazy(() => import("./components/experiences/BookingForm"));
const WaitlistForm = lazy(() => import("./components/experiences/WaitlistForm"));
const CancelBooking = lazy(() => import("./components/experiences/CancelBooking"));

const EVENT_NAME = process.env.REACT_APP_EVENT_NAME;
const UPDATE_POS = process.env.REACT_APP_UPDATE_POS;
const RESTRICT_EMAIL_DOMAINS = process.env.REACT_APP_RESTRICT_EMAIL_DOMAINS === 'true';

// Initialize Amplify - will be configured with actual values after deployment
Amplify.configure(amplifyconfig);

// ========================================
// TOGGLE THIS TO SHOW LOGO ONLY
// Set to true for clean white background with logo
// Set to false to show the full form
const SHOW_LOGO_ONLY = false;
// ========================================

// Check URL parameters before component mounts
const params = new URLSearchParams(window.location.search);
const onsite = params.get('onsite');
if (onsite === 'true' || onsite === 'false') {
  localStorage.setItem('onsite', onsite);
}

// Static English text constants
const TEXT = {
  eventInfo: EVENT_INFO,
  form: {
    firstName: "First Name *",
    lastName: "Last Name *",
    email: "Email Address *",
    country: "Country *",
    submit: "SIGN UP",
    shopNow: "SHOP THE EDIT"
  },
  disclaimers: {
    marketing: "I agree to receive the Mytheresa newsletter featuring updates on Maison Mytheresa, selected fashion offers, and I may also receive emails from LuxExperience B.V. I consent to the use of analytical web beacons within the newsletter and the creation of a personalized user profile based on my purchasing and browsing behavior. This may include notifications when I place items in my shopping cart. For more information, please refer to clause 5 of our {privacyPolicyLink}. I can withdraw my consent at any time by using the unsubscribe link included in every newsletter or by emailing privacy@mytheresa.com.",
    privacyPolicyUrl: PRIVACY_URLS.policyUrl,
    privacyPolicyText: DISCLAIMERS.privacyPolicyText
  },
  success: {
    dachNew: "Thank you. You will receive an email to confirm your registration shortly.",
    dachExisting: "Thank you. You will receive an email to confirm your registration shortly.",
    nonDach: "Thank you for registering."
  }
};

// Utility function to build Shop URL with UTM forwarding
function App() {
  useSkin();
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const [submitted, setSubmitted] = useState(false);
  const [emailProvidersVisible, setEmailProvidersVisible] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const emailRef = useRef(null);

  // Check if user is onsite
  const isOnsite = localStorage.getItem("onsite") === "true";

  // Track page view on mount with referral data for channel attribution
  useEffect(() => {
    const trackPageView = async () => {
      try {
        // Get pageview API URL from amplify config
        const pageviewUrl = amplifyconfig.custom?.pageviewApiUrl;
        if (pageviewUrl) {
          // Include referral data for marketing channel tracking
          const referralData = getReferralSource();
          await fetch(pageviewUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              pageId: 'registration',
              referral: {
                source: referralData.source,
                medium: referralData.medium,
                campaign: referralData.campaign,
                method: referralData.method,
                category: referralData.category,
                gclid: referralData.gclid,
                fbclid: referralData.fbclid,
              },
            }),
          });
        }
      } catch (error) {
        // Silently fail - page view tracking should not affect user experience
        logger.debug('Page view tracking failed:', error);
      }
    };
    trackPageView();
  }, []);

  const emailProviders = EMAIL_PROVIDERS;

  // If SHOW_LOGO_ONLY is true, render only the logo
  if (SHOW_LOGO_ONLY) {
    return (
      <>
        <LogoOnly />
        <CookieConsent />
      </>
    );
  }

  const onSubmit = async (data) => {
    data.event = EVENT_NAME;
    data.updatePOS = UPDATE_POS;
    data.onsite = isOnsite;
    data.language = 'en'; // Always English
    data.referral = getReferralSource(); // Add referral source tracking

    try {
      // Submit via Amplify API
      const response = await submitRegistration(data);

      if (response.ok) {
        // Store confirmation result from API response
        setConfirmationResult({
          confirmRequired: response.data.confirmRequired,
          isNewUser: response.data.isNewUser
        });
        setSubmitted(true);
        // Scroll to top when showing success message
        window.scrollTo({ top: 0, behavior: 'smooth' });
        // Shorter timeout for onsite users (3 seconds)
        setTimeout(() => {
          setSubmitted(false);
          setConfirmationResult(null);
          reset();
        }, isOnsite ? 10000 : 10000);
      } else {
        logger.error("Failed to submit data to the registration API");
      }
    } catch (error) {
      logger.error("An error occurred", error);
    }
  };

  const handleBlur = (field) => {
    const value = document.getElementById(field).value.trim();
    setValue(field, value);
  };

  const handleFocus = (field) => {
    if (field === "email") {
      setEmailProvidersVisible(false);
    } else {
      setEmailProvidersVisible(false);
    }
  };

  const handleEmailInput = () => {
    setEmailProvidersVisible(false);
  };

  const handleEmailProviderClick = (provider) => {
    const emailInput = emailRef.current;
    const value = emailInput.value.trim();
    const atIndex = value.indexOf("@");
    const newValue =
      atIndex !== -1
        ? value.substring(0, atIndex) + provider
        : value + provider;
    emailInput.value = newValue;
    setValue("email", newValue);
    setEmailProvidersVisible(false);
  };

  // Helper function to render disclaimer with privacy policy link
  const renderDisclaimerText = () => {
    const disclaimerText = TEXT.disclaimers.marketing;
    const privacyPolicyUrl = TEXT.disclaimers.privacyPolicyUrl;
    const privacyPolicyText = TEXT.disclaimers.privacyPolicyText;

    const parts = disclaimerText.split('{privacyPolicyLink}');

    return (
      <>
        {parts[0]}
        <a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer">
          {privacyPolicyText}
        </a>
        {parts[1]}
      </>
    );
  };


  // Check if we're on the dashboard page
  const isManagePage = window.location.pathname === '/manage';

  // Check if we're on the check-in page
  const isCheckinPage = window.location.pathname === '/checkin';

  // Check if we're on the experiences/events page (also serves as home page)
  const isEventsPage = window.location.pathname === '/' ||
                       window.location.pathname === '/events' ||
                       window.location.pathname === '/experiences';

  // Check if we're on a single experience page
  const isSingleExperiencePage = window.location.pathname.startsWith('/experience/') &&
                                  !window.location.pathname.endsWith('/book') &&
                                  !window.location.pathname.endsWith('/waitlist') &&
                                  !window.location.pathname.endsWith('/cancel');

  // Check if we're on the booking form page
  const isBookingPage = window.location.pathname.endsWith('/book');

  // Check if we're on the waitlist form page
  const isWaitlistPage = window.location.pathname.endsWith('/waitlist');

  // Check if we're on the cancel booking page
  // Token-based cancellation: /cancel/{token} (64-char hex token in path)
  const isCancelPage = window.location.pathname.startsWith('/cancel/');


  // If on manage page, render Dashboard component
  if (isManagePage) {
    return (
      <Suspense fallback={<LoadingSpinner fullScreen />}>
        <Dashboard />
      </Suspense>
    );
  }

  // If on check-in page, render CheckIn component
  if (isCheckinPage) {
    return (
      <Suspense fallback={<LoadingSpinner fullScreen />}>
        <CheckIn />
      </Suspense>
    );
  }

  // Experience routes share SessionsContext for centralized data caching
  const isExperienceRoute = isEventsPage || isSingleExperiencePage || isBookingPage || isWaitlistPage;

  if (isExperienceRoute) {
    return (
      <SessionsProvider>
        <Suspense fallback={<LoadingSpinner fullScreen />}>
          {isEventsPage && <AllExperiences />}
          {isSingleExperiencePage && <SingleExperience />}
          {isBookingPage && <BookingForm />}
          {isWaitlistPage && <WaitlistForm />}
        </Suspense>
      </SessionsProvider>
    );
  }

  // If on cancel booking page, render CancelBooking component
  if (isCancelPage) {
    return (
      <Suspense fallback={<LoadingSpinner fullScreen />}>
        <CancelBooking />
      </Suspense>
    );
  }

  // Show LogoOnly if SHOW_LOGO_ONLY is true
  if (SHOW_LOGO_ONLY) {
    return <LogoOnly />;
  }

  // Determine which success message to show based on confirmation result
  const getSuccessMessage = () => {
    if (!confirmationResult) {
      // Fallback to dachNew if no confirmation result
      return TEXT.success.dachNew;
    }

    if (confirmationResult.confirmRequired) {
      // DACH countries - need email confirmation
      if (confirmationResult.isNewUser) {
        return TEXT.success.dachNew;
      } else {
        return TEXT.success.dachExisting;
      }
    } else {
      // Non-DACH countries - auto-confirmed
      return TEXT.success.nonDach;
    }
  };

  if (submitted) {
    const successMsg = getSuccessMessage();

    return (
      <>
        <OrnateBackground>
          <header className="App-header">
            <div className="logo-container">
              <img
                src="/images/svg/Maison_Mytheresa_Logo.svg"
                alt={EVENT_INFO.imageAlt}
                className="logo-image"
              />
            </div>
            <img
              src="/images/svg/Line_object.svg"
              alt="Decorative line separator"
              className="separator-line"
            />
            <div className="success-message">
              <p>{successMsg}</p>
            </div>
            <img
              src="/images/svg/Line_object.svg"
              alt="Decorative line separator"
              className="separator-line"
            />
            <div className="luggage-cart-container">
              <img
                src="/images/luggage_cart.png"
                alt="Luggage Cart"
                className="luggage-cart-image"
              />
            </div>
          </header>
        </OrnateBackground>
        <Footer />
        <CookieConsent />
      </>
    );
  }

  return (
    <>
      <OrnateBackground>
        <header className="App-header">
            <div className="logo-container">
              <img
                src="/images/svg/Maison_Mytheresa_Logo.svg"
                alt={EVENT_INFO.imageAlt}
                className="logo-image"
              />
            </div>
            <img
              src="/images/svg/Line_object.svg"
              alt="Decorative line separator"
              className="separator-line"
            />
            {/* Only show event info if NOT onsite */}
            {!isOnsite && (
              <div className="event-info">
                <p className="dates-line">{TEXT.eventInfo.dates}</p>
                <p className="opening-days-line">{TEXT.eventInfo.openingDays}</p>
                <p className="hours-line">{TEXT.eventInfo.hours}</p>
                <p className="location-line">{TEXT.eventInfo.location}</p>
                <p className="address-line">{TEXT.eventInfo.address}</p>
              </div>
            )}
            <form className="form" onSubmit={handleSubmit(onSubmit)}>
          <div className="form-group">
            <input
              id="firstname"
              type="text"
              placeholder={TEXT.form.firstName}
              className={errors.firstname ? 'error' : ''}
              {...register("firstname", {
                required: true,
                pattern: /^[A-Za-z\s]+$/,
              })}
              onBlur={() => handleBlur("firstname")}
              onFocus={() => handleFocus("firstname")}
            />
          </div>

          <div className="form-group">
            <input
              id="lastname"
              type="text"
              placeholder={TEXT.form.lastName}
              className={errors.lastname ? 'error' : ''}
              {...register("lastname", {
                required: true,
                pattern: /^[A-Za-z\s]+$/,
              })}
              onBlur={() => handleBlur("lastname")}
              onFocus={() => handleFocus("lastname")}
            />
          </div>

          <div className="form-group">
            <input
              id="email"
              type="email"
              placeholder={TEXT.form.email}
              className={errors.email ? 'error' : ''}
              {...register("email", {
                required: true,
                pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                validate: (value) => {
                  if (!RESTRICT_EMAIL_DOMAINS) {
                    return true; // Allow all valid emails
                  }
                  const emailDomain = '@' + value.toLowerCase().split('@')[1];
                  const isAllowed = RESTRICTED_EMAIL_DOMAINS.some(d => d.toLowerCase() === emailDomain);
                  return isAllowed || `Only ${RESTRICTED_EMAIL_DOMAINS.join(' or ')} emails are allowed`;
                }
              })}
              onBlur={() => handleBlur("email")}
              onFocus={() => handleFocus("email")}
              onInput={handleEmailInput}
              ref={emailRef}
            />
            {emailProvidersVisible && (
              <div className="email-providers">
                {emailProviders.map((provider) => (
                  <button
                    type="button"
                    key={provider}
                    onClick={() => handleEmailProviderClick(provider)}
                  >
                    {provider}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="form-group">
            <select
              id="country"
              className={errors.country ? 'error' : ''}
              {...register("country", { required: true })}
              defaultValue=""
            >
              <option value="" disabled>{TEXT.form.country}</option>
              {COUNTRIES.map(country => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group checkbox-group">
            <div className="disclaimer-container">
              <div className="disclaimer-text">
                <label className="disclaimer-label">
                  <input
                    type="checkbox"
                    className={errors.disclaimerChecked1 ? 'error' : ''}
                    {...register("disclaimerChecked1", { required: true })}
                  />
                  <span className="disclaimer-label-text">{renderDisclaimerText()}</span>
                </label>
              </div>
            </div>
          </div>

          <button
            type="submit"
            className="form-button"
            disabled={isSubmitting}
            aria-busy={isSubmitting}
          >
            {isSubmitting ? (
              <span className="form-button-dots" aria-label="Submitting">
                <span className="form-button-dot" />
                <span className="form-button-dot" />
                <span className="form-button-dot" />
              </span>
            ) : (
              TEXT.form.submit
            )}
          </button>
        </form>
        {/* Only show Shop Now button if NOT onsite */}
        {!isOnsite && (
          <button
            type="button"
            onClick={() => window.open(buildShopUrl(), '_blank')}
            className="shop-now-button"
          >
            {TEXT.form.shopNow}
          </button>
        )}
          </header>
      </OrnateBackground>
            <Footer />
      <CookieConsent />
    </>
  );
}

export default App;
